import {createFeature, createReducer, createSelector, on} from '@ngrx/store';
import {EntityState, EntityAdapter, createEntityAdapter} from '@ngrx/entity';
import {AgentModel} from "../models/agent.model";
import {AgentsActions, AgentsAPiActions, AgentsChannelActions} from "./agents.actions";
import {uniq} from "lodash";

export interface AgentState extends EntityState<AgentModel> {
  cursor: string;
  isLoading: boolean;
  isInfiniteScrollLoading: boolean;
  infiniteScrollAgentIds: string[]
}

const adapter: EntityAdapter<AgentModel> = createEntityAdapter<AgentModel>();

const initialState: AgentState = adapter.getInitialState(
  {
    cursor: null,
    isLoading: false,
    isInfiniteScrollLoading: false,
    infiniteScrollAgentIds: []
  }
);

const reducer = createReducer(
  initialState,

  on(AgentsChannelActions.notificationReceived, (state, {notification}) => {
    if (state.entities[notification.id]?.updatedAt < notification.updatedAt) {
      return adapter.setOne(notification, state);
    } else {
      return state;
    }

  }),

  on(AgentsAPiActions.getAgentSuccess, (state, {agent}) =>
    adapter.setOne(agent, state)
  ),

  on(AgentsAPiActions.updateAgentSuccess, (state, {agent}) =>
    adapter.updateOne({
      id: agent.id,
      changes: {...agent}
    }, state)
  ),


  on(AgentsAPiActions.deleteAgentSuccess, (state, {agent}) =>
    adapter.removeOne(agent.id, state)
  ),

  on(AgentsAPiActions.getInfiniteScrollAgents, (state) => ({...state, isInfiniteScrollLoading: true})),

  on(AgentsAPiActions.getInfiniteScrollAgentsSuccess, (state, {agents, cursor}) => {
    return adapter.setMany(agents,
      {
        ...state,
        infiniteScrollAgentIds: uniq([...state.infiniteScrollAgentIds, ...agents.map(s => s.id)]),
        cursor: cursor,
        isInfiniteScrollLoading: false
      })
  }),

  on(AgentsAPiActions.getInfiniteScrollAgentsFailure, (state, {error}) => ({...state, isInfiniteScrollLoading: false})),


  on(AgentsActions.setInfiniteScrollAgentIds, (state, {agentIds, isAppendFirst}) => {
    let newState = state;
    const currentInfiniteScrollAgentIdsInStore = state.infiniteScrollAgentIds;

    if (isAppendFirst) {
      newState = {
        ...newState,
        infiniteScrollAgentIds: uniq([...agentIds, ...currentInfiniteScrollAgentIdsInStore])
      }
    } else {
      newState = {
        ...newState,
        infiniteScrollAgentIds: uniq([...currentInfiniteScrollAgentIdsInStore, ...agentIds])
      }
    }
    return newState;

  }),

  on(AgentsActions.removeAll, (state) => adapter.removeAll(state)),

  on(AgentsActions.clearStore, () => ({...initialState}))
);

export const agentsFeature = createFeature({
  name: 'Agents',
  reducer,
  extraSelectors: ({
                     selectEntities,
                     selectCursor,
                     selectIsLoading,
                     selectInfiniteScrollAgentIds,
                     selectIsInfiniteScrollLoading
                   }) => ({
    selectAgents: () => createSelector(
      selectEntities,
      (entities) => entities,
    ),
    selectAgent: (id: string) => createSelector(
      selectEntities,
      (entities) => entities[id],
    ),
    selectInfiniteScrollAgents: () => createSelector(
      selectEntities,
      selectInfiniteScrollAgentIds,
      (entities, infiniteScrollAgentIds) => {
        const infiniteScrollAgents = [];
        infiniteScrollAgentIds.forEach(id => {
          if (!!entities[id]) {
            infiniteScrollAgents.push(entities[id]);
          }
        })
        return infiniteScrollAgents;
      },
    ),
    selectCursor: () => createSelector(
      selectCursor,
      (cursor) => cursor
    ),
    selectIsInfiniteScrollLoading: () => createSelector(
      selectIsInfiniteScrollLoading,
      (isLoading) => isLoading
    ),
  }),
});

export const {
  selectIds,
  selectAll,
  selectTotal
} = adapter.getSelectors();
